/* global Element */
/**
 * hubspot-form-a11y.js
 *
 * Renders HubSpot (new forms editor, "v4") forms in the page DOM instead of an iframe and
 * closes the accessibility gaps HubSpot leaves (WCAG 2.1 AA 2.4.3 / 3.3.1 / 4.1.3).
 *
 * Markup (sections/hubspot-form.liquid):
 *   <div data-hubspot-a11y>
 *     <div class="hs-form-html" data-portal-id data-form-id data-region></div>
 *     <p role="status" data-hubspot-a11y-status></p>
 *   </div>
 *
 * 1. Loads HubSpot's "developer embed" loader (forms/embed/developer/{portalId}.js) once. It
 *    fetches server-rendered form HTML into `.hs-form-html`, a real <form> with labels,
 *    `novalidate`, `aria-required`, and HubSpot's own hutk / consent / submission handling.
 *    If HubSpot cannot serve that HTML the loader falls back to its iframe by itself.
 * 2. Error OWNERSHIP stays with HubSpot. Its React renderer already sets `aria-invalid` and
 *    wires each error through `aria-describedby`; opting the form into form-a11y-validation.js
 *    was measured to produce duplicate, orphaned errors (HubSpot's live validation re-renders
 *    the field and drops the helper's describedby token). See known-footguns.md.
 *    What HubSpot does NOT do is move focus, so after a submit (or step-navigation click)
 *    this moves focus to the first `aria-invalid="true"` control once HubSpot renders it.
 * 3. The post-submit thank-you HubSpot swaps in is copied into a pre-rendered role="status"
 *    region and receives focus, so screen-reader users hear that the form was sent.
 *
 * Safe to execute more than once: IIFE guarded by a window flag.
 */
(function () {
  'use strict';

  if (window.__mdHubspotFormA11y) return;
  window.__mdHubspotFormA11y = true;

  var ROOT = '[data-hubspot-a11y]';
  var TARGET = '.hs-form-html';
  var STATUS = '[data-hubspot-a11y-status]';
  var POST_SUBMIT = '.hsfc-PostSubmit';
  var INVALID =
    'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"], button[aria-invalid="true"]';
  // HubSpot validates client-side, then round-trips its validation endpoint.
  var WAIT_MS = 6000;

  var getAttr = Element.prototype.getAttribute;
  var setAttr = Element.prototype.setAttribute;
  var hasAttr = Element.prototype.hasAttribute;
  var qs = Element.prototype.querySelector;
  var qsa = Element.prototype.querySelectorAll;
  var closest = Element.prototype.closest;

  function loaderUrl(target) {
    var portalId = getAttr.call(target, 'data-portal-id') || '';
    var region = getAttr.call(target, 'data-region') || 'na1';
    if (!/^\d+$/.test(portalId) || !/^[a-z]{2}\d$/.test(region)) return '';
    var host =
      region === 'na1' ? 'js.hsforms.net' : 'js-' + region + '.hsforms.net';
    return 'https://' + host + '/forms/embed/developer/' + portalId + '.js';
  }

  // One loader per page: it watches the body itself and picks up containers added later.
  function requestLoader(target) {
    var src = loaderUrl(target);
    if (!src) return;
    var scripts = document.scripts;
    for (var i = 0; i < scripts.length; i++) {
      if (scripts[i].src === src) return;
    }
    var script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.body.appendChild(script);
  }

  function formIn(root) {
    var form = qs.call(root, TARGET + ' form');
    return form && closest.call(form, ROOT) === root ? form : null;
  }

  function focusFirstInvalid(form) {
    var field = qs.call(form, INVALID);
    if (!field) return false;
    if (document.activeElement !== field) field.focus();
    return true;
  }

  // After a submit attempt, wait for HubSpot to render its errors, then focus the first one.
  function watchAttempt(form) {
    if (form.__mdHubspotWatch) form.__mdHubspotWatch();
    var done = false;
    var observer = new MutationObserver(check);
    var timer = setTimeout(stop, WAIT_MS);
    function stop() {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      form.__mdHubspotWatch = null;
    }
    function check() {
      if (!done && form.isConnected && focusFirstInvalid(form)) stop();
    }
    form.__mdHubspotWatch = stop;
    observer.observe(form, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-invalid'],
    });
    // Errors from live (blur) validation may already be present with no further mutation.
    setTimeout(check, 0);
  }

  function squash(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  // textContent runs paragraphs and <br> lines together ("team.A representative").
  function readableText(source) {
    var el = source.cloneNode(true);
    var breaks = qsa.call(el, 'br');
    for (var b = 0; b < breaks.length; b++) {
      breaks[b].parentNode.replaceChild(
        document.createTextNode(' '),
        breaks[b]
      );
    }
    var blocks = qsa.call(el, 'p, h1, h2, h3, h4, h5, h6, li');
    if (!blocks.length) return squash(el.textContent);
    var parts = [];
    for (var i = 0; i < blocks.length; i++) {
      var text = squash(blocks[i].textContent);
      if (text) parts.push(text);
    }
    return parts.join(' ');
  }

  function announceSuccess(root) {
    var post = qs.call(root, POST_SUBMIT);
    if (!post || hasAttr.call(post, 'data-hubspot-a11y-announced')) return;
    setAttr.call(post, 'data-hubspot-a11y-announced', '');
    var status = qs.call(root, STATUS);
    if (status) status.textContent = readableText(post);
    if (!hasAttr.call(post, 'tabindex')) setAttr.call(post, 'tabindex', '-1');
    post.focus();
  }

  function enhance(root) {
    var form = formIn(root);
    // HubSpot's server HTML ships novalidate; keep the native bubble off regardless.
    if (form && !hasAttr.call(form, 'novalidate'))
      setAttr.call(form, 'novalidate', '');
    announceSuccess(root);
  }

  function init(root) {
    if (root.__mdHubspotA11y) return;
    root.__mdHubspotA11y = true;
    var target = qs.call(root, TARGET);
    if (target) requestLoader(target);
    enhance(root);
    new MutationObserver(function () {
      enhance(root);
    }).observe(root, { childList: true, subtree: true });
  }

  function rootOf(el) {
    return el instanceof Element ? closest.call(el, ROOT) : null;
  }

  // Capture phase, observe only: HubSpot's own submit handling must run untouched.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (rootOf(form)) watchAttempt(form);
    },
    true
  );

  // Multi-step forms validate on their "Next" button, which is not a submit.
  document.addEventListener(
    'click',
    function (event) {
      var button =
        event.target instanceof Element
          ? closest.call(event.target, 'button')
          : null;
      if (!button || !rootOf(button)) return;
      var form = button.form;
      if (form && getAttr.call(button, 'type') !== 'submit') watchAttempt(form);
    },
    true
  );

  function sweep() {
    var roots = qsa.call(document.documentElement, ROOT);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // The Theme Editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', sweep);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();
